/**
 * Admin API Service
 *
 * Service pour les appels API de l'interface d'administration
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost/signature-builder-backend';

// Helper pour les requetes
const adminFetch = async (endpoint, options = {}) => {
    const token = localStorage.getItem('admin_access_token');

    const config = {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
            ...options.headers
        }
    };

    const response = await fetch(`${API_URL}/api${endpoint}`, config);

    // Gerer l'expiration du token
    if (response.status === 401) {
        // Essayer de rafraichir le token
        const refreshToken = localStorage.getItem('admin_refresh_token');
        if (refreshToken) {
            try {
                const refreshResponse = await fetch(`${API_URL}/api/admin/auth/refresh`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ refresh_token: refreshToken })
                });

                if (refreshResponse.ok) {
                    const refreshData = await refreshResponse.json();
                    if (refreshData.data?.access_token) {
                        localStorage.setItem('admin_access_token', refreshData.data.access_token);
                        // Refaire la requete avec le nouveau token
                        config.headers['Authorization'] = `Bearer ${refreshData.data.access_token}`;
                        const retryResponse = await fetch(`${API_URL}/api${endpoint}`, config);
                        if (!retryResponse.ok) {
                            throw new Error(`HTTP error! status: ${retryResponse.status}`);
                        }
                        return retryResponse.json();
                    }
                }
            } catch (refreshError) {
                console.error('Token refresh failed:', refreshError);
            }
        }

        // Si le rafraichissement echoue, rediriger vers login
        localStorage.removeItem('admin_access_token');
        localStorage.removeItem('admin_refresh_token');
        window.location.href = '/admin/login';
        throw new Error('Session expiree');
    }

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.error || `HTTP error! status: ${response.status}`);
        error.response = { data: errorData, status: response.status };
        throw error;
    }

    return response.json();
};

export const adminApi = {
    // ========== Auth ==========

    login: (email, password) => {
        return fetch(`${API_URL}/api/admin/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password })
        }).then(res => res.json());
    },

    logout: (refreshToken) => {
        return adminFetch('/admin/auth/logout', {
            method: 'POST',
            body: JSON.stringify({ refresh_token: refreshToken })
        });
    },

    getMe: () => {
        return adminFetch('/admin/auth/me');
    },

    changePassword: (currentPassword, newPassword) => {
        return adminFetch('/admin/auth/password', {
            method: 'PUT',
            body: JSON.stringify({ current_password: currentPassword, new_password: newPassword })
        });
    },

    // ========== Dashboard ==========

    getStats: () => {
        return adminFetch('/admin/dashboard/stats');
    },

    getHealth: () => {
        return adminFetch('/admin/dashboard/health');
    },

    getHealthHistory: (hours = 24) => {
        return adminFetch(`/admin/dashboard/health/history?hours=${hours}`);
    },

    getActivityLogs: (page = 1, limit = 50) => {
        return adminFetch(`/admin/dashboard/logs?page=${page}&limit=${limit}`);
    },

    // ========== Organizations ==========

    getOrganizations: (params = {}) => {
        const searchParams = new URLSearchParams();
        if (params.page) searchParams.append('page', params.page);
        if (params.limit) searchParams.append('limit', params.limit);
        if (params.search) searchParams.append('search', params.search);
        if (params.status) searchParams.append('status', params.status);
        const query = searchParams.toString();
        return adminFetch(`/admin/organizations${query ? '?' + query : ''}`);
    },

    getOrganization: (id) => {
        return adminFetch(`/admin/organizations/${id}`);
    },

    createOrganization: (data) => {
        return adminFetch('/admin/organizations', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },

    updateOrganization: (id, data) => {
        return adminFetch(`/admin/organizations/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
    },

    deleteOrganization: (id) => {
        return adminFetch(`/admin/organizations/${id}`, {
            method: 'DELETE'
        });
    },

    toggleOrganizationStatus: (id) => {
        return adminFetch(`/admin/organizations/${id}/toggle-status`, {
            method: 'PUT'
        });
    },

    getOrganizationInvoices: (id) => {
        return adminFetch(`/admin/organizations/${id}/invoices`);
    },

    getOrganizationUsage: (id) => {
        return adminFetch(`/admin/organizations/${id}/usage`);
    },

    // ========== Users ==========

    toggleUserStatus: (userId) => {
        return adminFetch(`/admin/users/${userId}/toggle-status`, {
            method: 'PUT'
        });
    },

    // ========== Invoices ==========

    updateInvoiceStatus: (invoiceId, status) => {
        return adminFetch(`/admin/invoices/${invoiceId}/status`, {
            method: 'PUT',
            body: JSON.stringify({ status })
        });
    },

    // ========== Templates ==========

    getTemplates: () => {
        return adminFetch('/admin/templates');
    },

    getTemplate: (id) => {
        return adminFetch(`/admin/templates/${id}`);
    },

    createTemplate: (data) => {
        return adminFetch('/admin/templates', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },

    updateTemplate: (id, data) => {
        return adminFetch(`/admin/templates/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
    },

    deleteTemplate: (id) => {
        return adminFetch(`/admin/templates/${id}`, {
            method: 'DELETE'
        });
    },

    getCategories: () => {
        return adminFetch('/admin/templates/categories');
    },

    createCategory: (data) => {
        return adminFetch('/admin/templates/categories', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },

    deleteCategory: (id) => {
        return adminFetch(`/admin/templates/categories/${id}`, {
            method: 'DELETE'
        });
    }
};

export default adminApi;
